import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { CheckCircle2, Clock, Lock, History, X, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { checkPermission } from '../utils/userRoles';

const fmt = (n) => new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
}).format(Math.round(Number(n) || 0));

const fmtDt = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('es-CL', { timeZone: 'America/Santiago' });
};

function hoursLeft(fechaLimite) {
    if (!fechaLimite) return null;
    const ms = new Date(fechaLimite).getTime() - Date.now();
    return ms / 36e5;
}

export default function PreciosPendientesPage() {
    const { user } = useAuth();
    const canAuthorize = checkPermission(user, 'precios_pendientes_autorizar');
    const [rows, setRows] = useState([]);
    const [filtro, setFiltro] = useState('pendiente');
    const [loading, setLoading] = useState(true);
    const [authRow, setAuthRow] = useState(null);
    const [email, setEmail] = useState(user?.email || '');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [historyProduct, setHistoryProduct] = useState(null);
    const [history, setHistory] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_URL}/api/precios-pendientes?estado=${encodeURIComponent(filtro)}`);
            const data = await res.json().catch(() => []);
            setRows(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [filtro]);

    useEffect(() => { load(); }, [load]);

    const openAuth = (row) => {
        setAuthRow(row);
        setEmail(user?.email || '');
        setPassword('');
        setError('');
    };

    const submitAuth = async (e) => {
        e.preventDefault();
        if (!authRow) return;
        setSubmitting(true);
        setError('');
        try {
            const res = await apiFetch(`${API_URL}/api/precios-pendientes/${authRow.id}/autorizar`, {
                method: 'POST',
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'No se pudo autorizar');
            setAuthRow(null);
            setPassword('');
            await load();
            alert(data.message || 'Precio autorizado');
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const openHistory = async (row) => {
        setHistoryProduct(row);
        try {
            const res = await apiFetch(`${API_URL}/api/precios-pendientes/historial/${row.producto_id}`);
            const data = await res.json().catch(() => []);
            setHistory(Array.isArray(data) ? data : []);
        } catch {
            setHistory([]);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Precios pendientes de autorización</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            El Administrador autoriza con su usuario y contraseña. Sin autorización, el producto se bloquea al agotar el stock previo o a las 24 horas.
                        </p>
                    </div>
                    <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-200 inline-flex">
                        {['pendiente', 'autorizado', 'todos'].map((key) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setFiltro(key)}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                    filtro === key ? 'bg-indigo-500 text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {key === 'pendiente' ? 'Pendientes' : key === 'autorizado' ? 'Autorizados' : 'Todos'}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center text-gray-500">Cargando…</div>
                    ) : rows.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                            No hay registros en este filtro.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Producto</th>
                                        <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Tipo</th>
                                        <th className="text-right py-3 px-4 text-xs font-bold text-gray-400 uppercase">Costo / PPP</th>
                                        <th className="text-right py-3 px-4 text-xs font-bold text-gray-400 uppercase">Precio</th>
                                        <th className="text-right py-3 px-4 text-xs font-bold text-gray-400 uppercase">% gan.</th>
                                        <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Plazo</th>
                                        <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Estado</th>
                                        <th className="py-3 px-4" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {rows.map((row) => {
                                        const left = hoursLeft(row.fecha_limite);
                                        const overdue = row.estado === 'pendiente' && left != null && left <= 0;
                                        const urgent = row.estado === 'pendiente' && left != null && left > 0 && left <= 4;
                                        return (
                                            <tr key={row.id} className="hover:bg-indigo-50/30">
                                                <td className="py-3 px-4">
                                                    <div className="font-semibold text-gray-800">{row.producto_nombre}</div>
                                                    <div className="text-xs text-gray-400">
                                                        {row.codigo_barra || `ID ${row.producto_id}`}
                                                        {row.numero_factura ? ` · Fact. ${row.numero_factura}` : ''}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Ingresó {row.ingresado_por_nombre || '—'} · {fmtDt(row.fecha_creacion)}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                                        {row.tipo === 'margen_minimo' ? 'Margen mínimo' : 'Precio compra'}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <div className="text-xs text-gray-400">{fmt(row.costo_anterior)}</div>
                                                    <div className="font-semibold">{fmt(row.costo_nuevo)}</div>
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <div className="text-xs text-gray-400">{fmt(row.precio_anterior)}</div>
                                                    <div className="font-bold text-indigo-700">{fmt(row.precio_propuesto)}</div>
                                                </td>
                                                <td className="py-3 px-4 text-right font-medium">
                                                    {Number(row.porcentaje_ganancia || 0).toFixed(1)}%
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className={`text-xs font-medium ${overdue ? 'text-red-600' : urgent ? 'text-amber-600' : 'text-gray-600'}`}>
                                                        {fmtDt(row.fecha_limite)}
                                                    </div>
                                                    {row.estado === 'pendiente' && left != null && (
                                                        <div className="text-xs text-gray-400">
                                                            {overdue ? 'Plazo vencido' : `${left.toFixed(1)} h restantes`}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex flex-col gap-1">
                                                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full w-fit ${
                                                            row.estado === 'pendiente' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                                                        }`}>
                                                            {row.estado === 'pendiente' ? <Clock size={12} /> : <CheckCircle2 size={12} />}
                                                            {row.estado}
                                                        </span>
                                                        {row.precio_bloqueado && row.estado === 'pendiente' && (
                                                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-red-100 text-red-700 w-fit">
                                                                <Lock size={12} /> Bloqueado venta
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 text-right whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        onClick={() => openHistory(row)}
                                                        className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg inline-flex items-center gap-1"
                                                    >
                                                        <History size={14} /> Historial
                                                    </button>
                                                    {row.estado === 'pendiente' && canAuthorize && (
                                                        <button
                                                            type="button"
                                                            onClick={() => openAuth(row)}
                                                            className="ml-1 px-3 py-1.5 text-xs font-bold bg-indigo-500 text-gray-900 hover:bg-indigo-600 rounded-lg inline-flex items-center gap-1"
                                                        >
                                                            <ShieldCheck size={14} /> Autorizar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {authRow && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="auth-price-title">
                    <form onSubmit={submitAuth} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h2 id="auth-price-title" className="text-lg font-bold text-gray-800">Autorizar precio</h2>
                                <p className="text-sm text-gray-500 mt-1">{authRow.producto_nombre}</p>
                            </div>
                            <button type="button" onClick={() => setAuthRow(null)} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Cerrar">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="bg-indigo-50 rounded-xl p-3 text-sm mb-4">
                            <div className="flex justify-between"><span>Precio actual</span><span>{fmt(authRow.precio_anterior)}</span></div>
                            <div className="flex justify-between font-bold"><span>Precio nuevo</span><span>{fmt(authRow.precio_propuesto)}</span></div>
                            <div className="flex justify-between text-gray-600"><span>% ganancia</span><span>{Number(authRow.porcentaje_ganancia || 0).toFixed(1)}%</span></div>
                        </div>
                        <p className="text-xs text-gray-500 mb-3 flex items-start gap-2">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                            Ingrese el usuario y la contraseña de un Administrador. Al autorizar, el producto se desbloquea de inmediato.
                        </p>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1" htmlFor="admin-email">Usuario (email)</label>
                        <input
                            id="admin-email"
                            type="email"
                            required
                            autoComplete="username"
                            className="w-full mb-3 px-3 py-2 border border-gray-200 rounded-xl"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1" htmlFor="admin-pass">Contraseña</label>
                        <input
                            id="admin-pass"
                            type="password"
                            required
                            autoComplete="current-password"
                            className="w-full mb-3 px-3 py-2 border border-gray-200 rounded-xl"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        {error && <p className="text-sm text-red-600 mb-3" role="alert">{error}</p>}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 font-bold text-gray-900 disabled:bg-gray-300"
                        >
                            {submitting ? 'Autorizando…' : 'Autorizar y activar precio'}
                        </button>
                    </form>
                </div>
            )}

            {historyProduct && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
                    <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="font-bold text-gray-800">Historial — {historyProduct.producto_nombre}</h2>
                            <button type="button" onClick={() => setHistoryProduct(null)} className="p-1 text-gray-400" aria-label="Cerrar">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="overflow-y-auto p-4 space-y-2">
                            {history.length === 0 && <p className="text-sm text-gray-500">Sin movimientos.</p>}
                            {history.map((h) => (
                                <div key={h.id} className="border border-gray-100 rounded-xl p-3 text-sm">
                                    <div className="flex justify-between gap-2">
                                        <span className="font-semibold text-gray-700">{h.tipo_evento}</span>
                                        <span className="text-xs text-gray-400">{fmtDt(h.fecha_hora)}</span>
                                    </div>
                                    <div className="text-xs text-gray-500">{h.usuario_nombre || 'sistema'}</div>
                                    {(h.valor_anterior || h.valor_nuevo) && (
                                        <div className="text-xs mt-1">{h.valor_anterior || '—'} → {h.valor_nuevo || '—'}</div>
                                    )}
                                    {h.detalle && <div className="text-xs text-gray-600 mt-1">{h.detalle}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
